import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { connect } from 'react-redux';
import moment from 'moment';
import { I18n } from 'react-redux-i18n';
import { colorOf, withAlpha } from '../utils/ipadSource';
import {
	buildCategoryDiff,
	buildMenuDiff,
	cancelCategoryProductChanges,
	fetchCategoryProductCompare,
	hasCategoryProductChange,
	toIpadLinks,
	toIpadSupplementLinks,
} from '../utils/categoryChanges';
import { hasNoRegister, isIpadViewAvailable } from '../utils/ipadView';
import { getColors, getSelectedMenu, getVariableServer } from '../selectors/global.selector';
import { refreshChanges } from '../actions/patchMenu.action';
import { CATEGORY_CHANGES_CANCELLED } from '../constants/dayChanges';
import './IpadView.css';
import './CategoryChanges.css';

// Durées de l'animation « Rejouer » (ms) : départ, déplacements, arrivées.
const ANIM_START = 500;
const ANIM_MOVE = 1100;
const ANIM_END = 1900;

// Mises en page des grilles. Géométrie des cases animées en fractions de la
// largeur de la grille : largeur d'une case (tileW), pas entre deux cases en
// largeurs / hauteurs de case (stepX / stepY), hauteur / largeur (ratio).
// Origine de la modification (source draft_synchro).
const ORIGINS = { web_m: 'master', web_mc: 'local', web: 'local', webimp: 'import' };

const LAYOUTS = {
	// Cases produit : 3 colonnes, 6 px entre (comme .cc-grid).
	tiles: { columns: 3, tileW: 0.323, stepX: 1.045, stepY: 1.07, ratio: 47 / 66, className: '' },
	// Pastilles de suppléments de la caisse : 2 colonnes (comme .cc-pills).
	pills: { columns: 2, tileW: 0.492, stepX: 1.03, stepY: 1.14, ratio: 1 / 4, className: ' cc-pills', pill: true },
};

const position = (caseNumber, layout) => {
	const index = caseNumber - 1;
	const x = (index % layout.columns) * layout.stepX * 100;
	const y = Math.floor(index / layout.columns) * layout.stepY * 100;
	return `translate(${x}%, ${y}%)`;
};

// Fond d'une case : couleur pleine, ou pâle pour une pastille de supplément.
const background = (item, colors, pill) =>
	pill ? withAlpha(colorOf(item, colors), 0.2) : colorOf(item, colors);

// Note d'un produit de menu : prix (« 5,00 → 6,00 »), obligatoire / facultatif.
const noteLabel = note =>
	note === 'required' || note === 'optional' ? I18n.t('category_changes.' + note) : note;

const Cell = ({ cell, colors, number, className = '', badge, pill }) =>
	cell ? (
		<div
			className={'cc-cell ' + className + (cell.active != null && !Number(cell.active) ? ' cc-inactive' : '')}
			style={cell.status === 'removed' ? undefined : { backgroundColor: background(cell, colors, pill) }}>
			<div className="cc-name">{cell.name}</div>
			{cell.notes ? <span className="cc-note">{cell.notes.map(noteLabel).join(' · ')}</span> : null}
			{badge ? <span className="cc-badge">{badge}</span> : null}
			{number ? <span className="cc-num">{number}</span> : null}
		</div>
	) : (
		<div className="cc-cell cc-space" />
	);

const Grid = ({ cells, colors, numbered, layout }) => (
	<div className={'cc-grid' + layout.className}>
		{cells.map((cell, i) => (
			<Cell key={i} cell={cell} colors={colors} pill={layout.pill} number={numbered ? i + 1 : null} />
		))}
	</div>
);

/** Variante A : la grille d'arrivée annotée. */
const AnnotatedGrid = ({ diff, colors, layout }) => (
	<>
		<div className={'cc-grid' + layout.className}>
			{diff.middle.map((cell, i) => {
				if (!cell) return <Cell key={i} cell={null} />;
				const pill = layout.pill;
				if (cell.status === 'added') {
					return (
						<Cell key={i} cell={cell} colors={colors} pill={pill} className="cc-added" badge={I18n.t('category_changes.added')} />
					);
				}
				if (cell.status === 'moved') {
					return (
						<Cell key={i} cell={cell} colors={colors} pill={pill} className="cc-moved" badge={`${cell.from} → ${cell.to}`} />
					);
				}
				if (cell.status === 'removed') {
					return <Cell key={i} cell={cell} colors={colors} pill={pill} className="cc-removed" />;
				}
				return <Cell key={i} cell={cell} colors={colors} pill={pill} className={cell.notes ? 'cc-changed' : ''} />;
			})}
		</div>
		{diff.removedBelow.length ? (
			<div className={'cc-grid cc-removed-below' + layout.className}>
				{diff.removedBelow.map(cell => (
					<Cell
						key={cell.id}
						cell={cell}
						colors={colors}
						pill={layout.pill}
						className="cc-removed"
						badge={I18n.t('category_changes.was_case', { n: cell.from })}
					/>
				))}
			</div>
		) : null}
	</>
);

/** Variante B : la grille glisse de la caisse au brouillon. */
const AnimatedGrid = ({ diff, colors, phase, layout }) => {
	const cases = Math.max(diff.before.length, diff.after.length, 1);
	const rows = Math.ceil(cases / layout.columns);
	const height = ((rows - 1) * layout.stepY + 1) * layout.tileW * layout.ratio;
	return (
		<div className={'cc-anim' + layout.className} style={{ paddingBottom: `${height * 100}%` }}>
			{Array.from({ length: cases }, (_, i) => (
				<div
					key={'slot-' + i}
					className="cc-cell cc-space cc-tile cc-slot"
					style={{ width: `${layout.tileW * 100}%`, transform: position(i + 1, layout) }}
				/>
			))}
			{diff.moves.map(move => {
				const removed = move.to === null;
				const added = move.from === null;
				const at = phase === 0 ? move.from || move.to : move.to || move.from;
				let className = 'cc-cell cc-tile';
				if (phase > 0 && removed) className += ' cc-removed cc-fade';
				if (added) className += ' cc-added' + (phase < 2 ? ' cc-hidden' : '');
				if (phase > 0 && !added && !removed && move.from !== move.to) className += ' cc-moved';
				return (
					<div
						key={move.id}
						className={className}
						style={{
							width: `${layout.tileW * 100}%`,
							transform: position(at, layout),
							backgroundColor: phase > 0 && removed ? undefined : background(move, colors, layout.pill),
						}}>
						<div className="cc-name">{move.name}</div>
					</div>
				);
			})}
		</div>
	);
};

/** Titre d'un niveau de menu ; au milieu, ce qui change sur le niveau. */
const LevelTitle = ({ section, title, annotated }) => {
	if (title == null) return null;
	if (!annotated) return <div className="cc-level">{title}</div>;
	const tags = [];
	if (section.status === 'added') tags.push(['added', I18n.t('category_changes.added')]);
	if (section.status === 'removed') tags.push(['removed', I18n.t('category_changes.legend_removed')]);
	if (section.status === 'same' && section.from !== section.to) {
		tags.push(['moved', I18n.t('category_changes.level_moved', { from: section.from, to: section.to })]);
	}
	return (
		<div className={'cc-level' + (section.status === 'removed' ? ' cc-level-removed' : '')}>
			{section.renamed ? `${section.beforeTitle} → ${title}` : title}
			{tags.map(([className, label]) => (
				<span key={className} className={'cc-level-tag cc-tag-' + className}>
					{label}
				</span>
			))}
		</div>
	);
};

// Une seule grille (catégorie) : une section sans titre.
const singleSection = diff => ({ sections: [{ id: 'grid', diff, unchanged: diff.unchanged }], unchanged: diff.unchanged });

export const CategoryChangesOverlay = ({
	idMenu,
	idCategory,
	date,
	idZone,
	colors,
	hideDisabled,
	noRegister,
	onClose,
	onCompared,
	onCancelled,
	kind = 'product',
}) => {
	const supplement = kind === 'supplement';
	const menu = kind === 'menu';
	const [state, setState] = useState({ status: 'loading' });
	// Affichage : 'grid' (positions du back-office) ou 'ipad' (rendu de la
	// caisse : indisponibles retirés si elle les cache, suivants remontés).
	// iPad par défaut ; au siège et en zone, grille seulement : il n'y a pas
	// de caisse.
	const [selectedMode, setMode] = useState('ipad');
	const mode = noRegister ? 'grid' : selectedMode;
	const [phase, setPhase] = useState(null); // null = grille annotée
	// Suppression : null, 'confirm', 'running' ou 'error'.
	const [cancel, setCancel] = useState(null);

	// Suppression confirmée : le serveur remet le brouillon à la caisse ; on
	// recharge tout (onCancelled) puis on ferme.
	const confirmCancel = () => {
		setCancel('running');
		cancelCategoryProductChanges({ idMenu, idCategory, kind }).then(
			() => {
				if (onCancelled) onCancelled();
				onClose();
			},
			() => setCancel('error')
		);
	};

	const replay = useCallback(() => {
		setPhase(0);
		const timers = [
			setTimeout(() => setPhase(1), ANIM_START),
			setTimeout(() => setPhase(2), ANIM_MOVE),
			setTimeout(() => setPhase(null), ANIM_MOVE + ANIM_END),
		];
		return () => timers.forEach(clearTimeout);
	}, []);

	useEffect(() => {
		let alive = true;
		fetchCategoryProductCompare({ idMenu, idCategory, date, idZone, kind }).then(
			result => {
				if (!alive) return;
				// Rendu caisse : les suppléments s'enchaînent (indisponibles grisés),
				// les produits (et ceux d'un niveau de menu) suivent la règle de la
				// Vue caisse.
				const ipadLinks = (links, products) =>
					supplement ? toIpadSupplementLinks(links) : toIpadLinks(links, products, hideDisabled);
				// Les deux affichages sont calculés d'emblée : basculer est immédiat.
				let views = null;
				if (result.found && menu) {
					views = { grid: buildMenuDiff(result), ipad: buildMenuDiff(result, ipadLinks) };
				} else if (result.found) {
					views = {
						grid: singleSection(buildCategoryDiff(result)),
						ipad: singleSection(
							buildCategoryDiff({
								...result,
								before: ipadLinks(result.before, result.productsBefore),
								after: ipadLinks(result.after, result.productsAfter),
							})
						),
					};
				}
				setState(
					views ? { status: 'done', origin: ORIGINS[result.source] || null, views } : { status: 'none' }
				);
				if (onCompared) onCompared();
			},
			() => alive && setState({ status: 'error' })
		);
		return () => {
			alive = false;
		};
	}, [idMenu, idCategory, date, idZone, kind, supplement, menu, hideDisabled, onCompared]);

	// Lecture automatique à l'ouverture, dans l'affichage d'arrivée (le mode
	// n'est volontairement pas une dépendance : basculer ne relance rien).
	useEffect(() => {
		if (state.status === 'done' && !state.views[mode].unchanged) return replay();
	}, [state, replay]);

	useEffect(() => {
		const onKey = e => e.key === 'Escape' && onClose();
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [onClose]);

	let message = null;
	if (state.status === 'loading') message = I18n.t('category_changes.loading');
	else if (state.status === 'error') message = I18n.t('category_changes.error');
	else if (state.status === 'none') message = I18n.t('category_changes.none');
	const view = state.views ? state.views[mode] : null;
	// Suppléments en affichage iPad : pastilles de la caisse, 2 colonnes.
	const layout = supplement && mode === 'ipad' ? LAYOUTS.pills : LAYOUTS.tiles;
	if (view && view.unchanged) message = I18n.t('category_changes.unchanged');
	let title = 'category_changes.title';
	if (supplement) title = 'category_changes.title_supplement';
	if (menu) title = 'category_changes.title_menu';

	return createPortal(
		<div className="ipad-view-backdrop" onClick={onClose}>
			<div className="cc-screen" onClick={e => e.stopPropagation()}>
				<div className="ipad-view-header">
					<span>{I18n.t(title)}</span>
					<IconButton onClick={onClose} aria-label={I18n.t('close')}>
						<CloseIcon />
					</IconButton>
				</div>
				{/* Supprimer les modifications en cours, en deux temps (confirmation). */}
				{state.status === 'done' ? (
					<div className="cc-actions">
						<div className="cc-mode">
							{(noRegister ? [] : ['grid', 'ipad']).map(m => (
								<Button
									key={m}
									size="small"
									variant={mode === m ? 'contained' : 'outlined'}
									className={'cc-mode-' + m}
									onClick={() => setMode(m)}>
									{I18n.t('category_changes.mode_' + m)}
								</Button>
							))}
							{state.origin ? (
								<span className={'cc-origin cc-origin-' + state.origin}>
									{I18n.t('category_changes.origin_' + state.origin)}
								</span>
							) : null}
						</div>
						{cancel === 'confirm' || cancel === 'running' ? (
							<>
								<span>{I18n.t('category_changes.cancel_confirm')}</span>
								<Button onClick={() => setCancel(null)} disabled={cancel === 'running'}>
									{I18n.t('category_changes.cancel_no')}
								</Button>
								<Button
									variant="contained"
									className="cc-cancel-yes"
									onClick={confirmCancel}
									disabled={cancel === 'running'}>
									{I18n.t('category_changes.cancel_yes')}
								</Button>
							</>
						) : (
							<>
								{cancel === 'error' ? (
									<span className="cc-cancel-error">{I18n.t('category_changes.cancel_error')}</span>
								) : null}
								<Button variant="outlined" className="cc-cancel" onClick={() => setCancel('confirm')}>
									{I18n.t('category_changes.cancel')}
								</Button>
							</>
						)}
					</div>
				) : null}
				{message ? <div className="ipad-view-message">{message}</div> : null}
				{view ? (
					<div className="cc-columns">
						<div className="cc-col">
							<h4>{I18n.t('category_changes.draft')}</h4>
							{view.sections
								.filter(section => section.status !== 'removed')
								.map(section => (
									<div key={section.id} className="cc-section">
										<LevelTitle section={section} title={section.title} />
										<Grid cells={section.diff.after} colors={colors} numbered layout={layout} />
									</div>
								))}
						</div>
						<div className="cc-col cc-col-middle">
							<h4>
								{I18n.t('category_changes.diff')}
								<Button size="small" onClick={replay} disabled={phase !== null}>
									▶ {I18n.t('category_changes.replay')}
								</Button>
							</h4>
							{view.sections.map(section => (
								<div key={section.id} className="cc-section">
									<LevelTitle section={section} title={section.title} annotated />
									{phase === null ? (
										<AnnotatedGrid diff={section.diff} colors={colors} layout={layout} />
									) : (
										<AnimatedGrid diff={section.diff} colors={colors} phase={phase} layout={layout} />
									)}
								</div>
							))}
							<div className="cc-legend">
								<span className="cc-dot cc-dot-added" />
								{I18n.t('category_changes.legend_added')}
								<span className="cc-dot cc-dot-moved" />
								{I18n.t('category_changes.legend_moved')}
								<span className="cc-dot cc-dot-removed" />
								{I18n.t('category_changes.legend_removed')}
								{menu ? (
									<>
										<span className="cc-dot cc-dot-changed" />
										{I18n.t('category_changes.legend_changed')}
									</>
								) : null}
							</div>
						</div>
						<div className="cc-col">
							<h4>{I18n.t('category_changes.caisse')}</h4>
							{view.sections
								.filter(section => section.status !== 'added')
								.sort((a, b) => a.from - b.from)
								.map(section => (
									<div key={section.id} className="cc-section">
										<LevelTitle section={section} title={section.beforeTitle} />
										<Grid cells={section.diff.before} colors={colors} numbered layout={layout} />
									</div>
								))}
						</div>
					</div>
				) : null}
			</div>
		</div>,
		document.body
	);
};

CategoryChangesOverlay.propTypes = {
	idMenu: PropTypes.string,
	idCategory: PropTypes.string.isRequired,
	date: PropTypes.string,
	idZone: PropTypes.string,
	colors: PropTypes.array,
	hideDisabled: PropTypes.bool,
	noRegister: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onCompared: PropTypes.func,
	onCancelled: PropTypes.func,
	kind: PropTypes.oneOf(['product', 'supplement', 'menu']),
};

/**
 * Bouton « Modifications en cours » de la grille d'une catégorie (kind
 * 'product'), d'une catégorie de suppléments (kind 'supplement') ou de la
 * composition d'un menu (kind 'menu', idCategory = le menu). Toujours
 * affiché, grisé si aucune modification non déployée ne touche la grille.
 */
export const CategoryChangesButton = ({ hasChanges, style, refresh, reload, ...overlayProps }) => {
	const [open, setOpen] = useState(false);
	const close = useCallback(() => setOpen(false), []);

	return (
		<div style={style}>
			<Button variant="contained" disabled={!hasChanges && !open} onClick={() => setOpen(true)}>
				{I18n.t('category_changes.button')}
			</Button>
			{open ? <CategoryChangesOverlay {...overlayProps} onClose={close} onCompared={refresh} onCancelled={reload} /> : null}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { variableServer, dayChanges } = state.entity;
	// Journée affichée, comme l'écran « Modifications en cours ».
	const date = moment(getVariableServer(variableServer, 'selected_date') || undefined).format('YYYY-MM-DD');
	const idZone = window.sessionStorage.getItem('id_zone');
	return {
		hasChanges: hasCategoryProductChange(
			dayChanges && dayChanges.list,
			date,
			idZone,
			ownProps.idCategory,
			ownProps.kind
		),
		idMenu: getSelectedMenu(variableServer),
		date,
		idZone,
		colors: getColors(state),
		// Affichage iPad : même règle que la Vue caisse.
		hideDisabled: isIpadViewAvailable(state.entity.variable),
		// Siège et zone : pas de caisse, donc pas d'affichage iPad.
		noRegister: hasNoRegister({
			isMaster: !!state.user.master,
			accessMode: state.access.mode,
			masterMonoChild: window.sessionStorage.getItem('master_mono_child'),
			idZone,
		}),
	};
};

// old_value recalée : le décompte des modifications en cours peut changer.
// Modification supprimée : rechargement complet (grille, décompte, siège).
const mapDispatchToProps = dispatch => ({
	refresh: () => dispatch(refreshChanges()),
	reload: () => dispatch({ type: CATEGORY_CHANGES_CANCELLED }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CategoryChangesButton);
